'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import type { PredictionSide, PricePredictionHistoryItem } from '@flipster/apis/v1/price-prediction';
import { FormattedMessage } from '@flipster/i18n';
import { Table } from '@flipster/react/design-system-legacy/table';
import { Badge } from '@flipster/react/design-system/badge';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { EmptyStateIconography } from '@flipster/react/graphic-assets/iconography/EmptyStateIconography';
import ArrowDownRightIcon from '@flipster/react/icon/generated/ArrowDownRightIcon';
import ArrowUpRightIcon from '@flipster/react/icon/generated/ArrowUpRightIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';

import { SeparatorTitle } from '../../component/separator-title';
import { pricePredictionBunja } from './state';

const COLUMNS = [
  { key: 'date', label: <FormattedMessage id='Campaign.r260123.Date' /> },
  { key: 'prediction', label: <FormattedMessage id='Campaign.r260123.My_price_prediction_non_capital' /> },
  { key: 'volume', label: <FormattedMessage id='Campaign.r260123.Trading_volume_usdt' /> },
  { key: 'btcPrice', label: <FormattedMessage id='Campaign.r260123.BTC_price_at_start_usdt' /> },
  { key: 'btcResult', label: <FormattedMessage id='Campaign.r260123.BTC_price_at_end_usdt' /> },
  { key: 'reward', label: <FormattedMessage id='Campaign.r260123.Reward_usdt' /> },
];

export const PredictionHistory = () => {
  const { pricePredictionMeAtom } = useBunja(pricePredictionBunja);
  const me = useAtomValue(pricePredictionMeAtom);
  const history = me?.history ?? [];
  return (
    <section className={vstack({ gap: 24, w: '100%', alignItems: 'stretch' })}>
      <SeparatorTitle
        colorSet='keyColor'
        title={<FormattedMessage id='Campaign.r260123.Price_predictions_history' />}
      />

      {/* Desktop Table */}
      <div className={css({ display: { base: 'none', tablet: 'block' }, overflowX: 'auto' })}>
        <Table noData={<EmptyState />}>
          <Table.Header>
            <Table.Row>
              {COLUMNS.map((col, index) => (
                <Table.HeaderCell
                  key={col.key}
                  justify={index >= 3 ? 'end' : 'start'}
                  css={{ textStyle: 'Text/S/Regular' }}
                >
                  {col.label}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {history.map((item, index) => (
              <Table.Row key={index}>
                <Table.BodyCell css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-5' }}>
                  <Datetime value={item.roundStartTime} format='yyyy.MM.dd' />
                </Table.BodyCell>
                <Table.BodyCell>
                  <PredictionBadge side={item.predictionSide} />
                </Table.BodyCell>
                <Table.BodyCell css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' }}>
                  {item.predictionSide == null ? '-' : <Formatted.Number value={item.volume} />}
                </Table.BodyCell>
                <Table.BodyCell
                  justify='end'
                  css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' }}
                >
                  <Formatted.Number value={item.snapshotPrice} toFixed precision={UsdtPrecision} />
                </Table.BodyCell>
                <Table.BodyCell
                  justify='end'
                  css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' }}
                >
                  <Formatted.Number value={item.resultPrice} toFixed precision={UsdtPrecision} />
                </Table.BodyCell>
                <Table.BodyCell
                  justify='end'
                  css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' }}
                >
                  {item.predictionSide == null ? (
                    <FormattedMessage id='Campaign.r260123.Ineligible' />
                  ) : (
                    <Formatted.Number
                      value={item.reward ?? '0'}
                      withColor
                      prefix={Number(item.reward) > 0 ? '+' : ''}
                      toFixed
                      precision={UsdtPrecision}
                    />
                  )}
                </Table.BodyCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className={css({ display: { base: 'flex', tablet: 'none' }, flexDirection: 'column', gap: 12 })}>
        {history.length === 0 ? <EmptyState /> : history.map((item, index) => <HistoryCard key={index} item={item} />)}
      </div>
    </section>
  );
};

const HistoryCard = ({ item }: { item: PricePredictionHistoryItem }) => {
  const isIneligible = item.predictionSide == null;

  return (
    <div
      className={vstack({
        gap: 4,
        py: 12,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderColor: 'border.default.level-1',
        alignItems: 'stretch',
      })}
    >
      <div
        className={hstack({
          justifyContent: 'space-between',
          pb: 10,
          borderBottomWidth: '1px',
          borderBottomStyle: 'dashed',
          borderColor: 'border.default.level-1',
        })}
      >
        <Datetime
          value={item.roundStartTime}
          format='yyyy.MM.dd'
          css={{ textStyle: 'Text/L/Regular', color: 'inverse.white' }}
        />
        <PredictionBadge side={item.predictionSide} />
      </div>

      <div className={vstack({ gap: 0, alignItems: 'stretch' })}>
        <InfoRow
          label={<FormattedMessage id='Campaign.r260123.Trading_volume_usdt' />}
          value={isIneligible ? '-' : <Formatted.Number value={item.volume} />}
        />
        <InfoRow
          label={<FormattedMessage id='Campaign.r260123.BTC_price_at_start_usdt' />}
          value={<Formatted.Number value={item.snapshotPrice} toFixed precision={UsdtPrecision} />}
        />
        <InfoRow
          label={<FormattedMessage id='Campaign.r260123.BTC_price_at_end_usdt' />}
          value={<Formatted.Number value={item.resultPrice} toFixed precision={UsdtPrecision} />}
        />
        <InfoRow
          label={<FormattedMessage id='Campaign.r260123.Reward_usdt' />}
          value={
            isIneligible ? (
              <span className={css({ color: 'content.default.level-3' })}>
                <FormattedMessage id='Campaign.r260123.Ineligible' />
              </span>
            ) : (
              <span
                className={css({
                  color: Number(item.reward) > 0 ? 'content.state.positive' : 'content.default.level-3',
                })}
              >
                <Formatted.Number
                  value={item.reward ?? '0'}
                  prefix={Number(item.reward) > 0 ? '+' : ''}
                  toFixed
                  precision={UsdtPrecision}
                />
              </span>
            )
          }
        />
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => {
  return (
    <div className={hstack({ gap: 12, alignItems: 'center' })}>
      <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>{label}</span>
      <span
        className={css({
          textStyle: 'Text/L/Monospace/Regular',
          color: 'content.default.level-3',
          flex: 1,
          textAlign: 'right',
        })}
      >
        {value}
      </span>
    </div>
  );
};

const EmptyState = () => {
  return (
    <div
      className={vstack({
        w: '100%',
        py: 120,
        px: 12,
        gap: 8,
        alignItems: 'center',
      })}
    >
      <EmptyStateIconography size={40} />
      <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Common.r251015.No_data_found' />
      </span>
    </div>
  );
};

interface PredictionBadgeProps {
  side: PredictionSide | undefined;
}

const PredictionBadge = ({ side }: PredictionBadgeProps) => {
  if (!side) {
    return <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>-</span>;
  }

  const isUp = side === 'UP';

  return (
    <Badge
      type='outline'
      color={isUp ? 'positive' : 'negative'}
      leadingIcon={isUp ? <ArrowUpRightIcon /> : <ArrowDownRightIcon />}
    >
      {isUp ? (
        <FormattedMessage id='Campaign.r260123.Going_up' />
      ) : (
        <FormattedMessage id='Campaign.r260123.Going_down' />
      )}
    </Badge>
  );
};
